import React from 'react';
import { ChakraProvider, Box, Flex, useColorMode, createStandaloneToast } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import customTheme from './theme';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import AssistantPage from './pages/AssistantPage';
import PhoneNumberPage from './pages/PhoneNumberPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import ToolPage from './pages/ToolPage';
import SettingsPage from './pages/SettingsPage';
import ApiKeysPage from './pages/ApiKeysPage';
import ApiDocsPage from './pages/ApiDocsPage';
import ProfilePage from './pages/ProfilePage';
import BillingPage from './pages/BillingPage';
import AdminCreateUserPage from './pages/AdminCreateUserPage';
import PasswordResetPage from './pages/PasswordResetPage';
import AdminAllUsersPage from './pages/AdminAllUsersPage';
import AdminListCollectionsPage from './pages/AdminListCollectionsPage';
import './App.css';
import MCPPage from './pages/MCPPage';
import AdminModelsPage from './pages/AdminModelsPage';
import AdminUnbilledPage from './pages/AdminUnbilledPage';
import AdminInvoicesPage from './pages/AdminInvoicesPage';
import InvoicesPage from './pages/InvoicesPage';

const { ToastContainer } = createStandaloneToast();

const AppContent = () => {
  const { colorMode } = useColorMode();

  return (
    <Router>
      <Flex minH="100vh" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
        <Box flex="1" p={4} overflow="auto">
          <Routes>
            {/* Default route redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<PasswordResetPage />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assistant"
              element={
                <ProtectedRoute>
                  <AssistantPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/phone-numbers"
              element={
                <ProtectedRoute>
                  <PhoneNumberPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/knowledgebase"
              element={
                <ProtectedRoute>
                  <KnowledgeBasePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tool"
              element={
                <ProtectedRoute>
                  <ToolPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mcp"
              element={
                <ProtectedRoute>
                  <MCPPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/api-keys"
              element={
                <ProtectedRoute>
                  <ApiKeysPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/api-docs"
              element={
                <ProtectedRoute>
                  <ApiDocsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <BillingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/create-user"
              element={
                <AdminProtectedRoute>
                  <AdminCreateUserPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminProtectedRoute>
                  <AdminAllUsersPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/collections"
              element={
                <AdminProtectedRoute>
                  <AdminListCollectionsPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/models"
              element={
                <AdminProtectedRoute>
                  <AdminModelsPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/unbilled"
              element={
                <AdminProtectedRoute>
                  <AdminUnbilledPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/invoices"
              element={
                <AdminProtectedRoute>
                  <AdminInvoicesPage />
                </AdminProtectedRoute>
              }
            />
          </Routes>
        </Box>
      </Flex>
    </Router>
  );
};

const App = () => {
  return (
    <ChakraProvider theme={customTheme}>
      <AppContent />
      <ToastContainer />
    </ChakraProvider>
  );
};

export default App;
